<template>
  <div class="flex flex-col justify-between flex-1">
    <div v-if="!$store.state.integration.integrations.find((i) => i.name == 'slack')">
      <span>{{ $t("Slack integration required") }}</span>
      <a href="/admin/integrations/slack" class="text-blue-600 ml-2 btn cancel-btn"> {{ $t("Link") }} </a>
    </div>
    <div v-else class="pt-6 pb-5 space-y-6">
      <Form :initial-values="initial_values" :validation-schema="schema" class="mt-2 space-y-3" @submit="sendSlack">
        <div v-if="inPlaybook" class="p-2 border rounded-md dark:border-dark-divider-border">
          <div class="m-1 text-lg text-common-grey">
            {{ $t("Sender") }}:
          </div>
          <div class="flex items-center gap-1 rounded-md shadow-sm">
            <SenderSlackAutoCompleteInput
              v-model:selected-sender="selected_sender"
              :placeholder="$t('Sender')"
              input-classes="placeholder-common-grey"
              :senders="
                autocomplete_options_in_playbook_with_values_for_sender.filter((o) =>
                  o.value.id ? true : typeof o.value == 'string' && !o.value?.includes('co_owners')
                )
              " />
          </div>
          <div class="flex flex-row flex-wrap gap-2 mt-1">
            <!-- For Selected sender -->
            <ContactCard
              v-if="selected_sender?.id"
              :key="selected_sender?.id"
              :slack="true"
              :contact="selected_sender"
              :contact-type="'recipient'"
              :ellipsis-vertical-icon="EllipsisVertical"
              @removeContact="handleRemoveSender" />
          </div>
        </div>
        <div class="p-2 border rounded-md dark:border-dark-divider-border">
          <div class="m-1 text-lg text-common-grey">
            {{ $t("Recipients") }}:
          </div>
          <div class="flex items-center gap-1 rounded-md shadow-sm">
            <SlackContactsAutoCompleteInput
              :hide-people-tab="selected_channels.length"
              :hide-channels-tab="selected_users.length"
              :placeholder="$t('Send to')"
              input-classes="placeholder-common-grey"
              :autocomplete-options="autocomplete_options_in_playbook_with_values_for_users"
              :selected-contacts="selected_users"
              :selected-channels="selected_channels"
              @submitted="handleAddItem"
              @chosen="handleAddItem" />
          </div>
          <div class="flex flex-row flex-wrap gap-2 mt-1">
            <!-- For recipient contacts -->
            <ContactCard
              v-for="contact in selected_users"
              :key="contact.id"
              :slack="true"
              :contact="contact"
              :contact-type="'recipient'"
              :ellipsis-vertical-icon="EllipsisVertical"
              @removeContact="handleRemoveUser" />

            <ChannelCard
              v-for="channel in selected_channels"
              :key="channel.id"
              :channel="channel"
              :ellipsis-vertical-icon="EllipsisVertical"
              @removeChannel="handleRemoveChannel" />
          </div>
        </div>

        <div class="mt-2 relative">
          <div>{{ $t("Message") }}:</div>

          <div class="absolute top-1 right-4">
            <div
              v-if="resultReferencesListForText.length"
              :class="[resultReferencesListForText.length ? 'opacity-100' : 'opacity-0']"
              class="inset-y-0 z-10 flex items-center pr-3 transition-all duration-500 cursor-pointer"
              @click="show_autocomplete_options = !show_autocomplete_options">
              <DynamicIcon
                :color="$store.state.ui.darkMode ? '#fff' : '#899399'"
                class="w-5 h-5 transition ease-in-out duration-side-menu-transition-duration" />
            </div>

            <transition
              v-show="resultReferencesListForText.length && show_autocomplete_options"
              leave-active-class="transition ease-in duration-100"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0">
              <div
                static
                class="absolute mt-1 w-44 -ml-44 bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm dark:bg-dark-box-background z-20">
                <div v-for="item in resultReferencesListForText" :key="item.value">
                  <li
                    class="flex items-center text-gray-900 cursor-pointer hover:bg-gray-200 relative py-2 pl-2 pr-4 dark:text-white dark:hover:bg-transparent"
                    @click.prevent="handleSelectOption(item)">
                    <span class="font-normal ml-3 block"> {{ item.label }} </span>
                  </li>
                </div>
              </div>
            </transition>
          </div>

          <div class="relative mb-20 h-52 border rounded-lg">
            <TextEditor
              ref="quill"
              v-model:content="body_content"
              class="dark:text-white"
              content-type="delta"
              :mention-items="resultReferencesListForText"
              theme="bubble"
              :toolbar="false" />
          </div>

          <AttachmentsField v-model="attachments" :overwrite-label="$t('Assets')" :options="['file']" class="w-full mt-4 mb-10" />

          <div class="relative flex flex-row justify-between w-full gap-2 mb-5 top-5" :class="{ 'relative !mt-20': inPlaybook }">
            <button type="button" class="w-1/2 m-0 cancel-btn" @click="$emit('cancel')">{{ $t("Cancel") }}</button>
            <button :class="{ 'pointer-events-none opacity-40': form_incomplete }" type="submit" class="w-1/2 primary-btn">
              {{ inPlaybook ? $t("Save") : $t("Send") }}
            </button>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script>
import * as Yup from "yup"
import { Form } from "vee-validate"
import SenderSlackAutoCompleteInput from "@/components/shared/SenderSlackAutoCompleteInput.vue"
import TextEditor from "@/components/shared/TextEditor.vue"
import { EllipsisVertical } from "lucide-vue-next"
import dataService from "@/services/data.service"
import Delta from "quill-delta"
import AttachmentsField from "@/components/shared/form-inputs/AttachmentsField.vue"
import ContactCard from "@/components/forms/email/ContactCard.vue"
import ChannelCard from "@/components/forms/email/ChannelCard.vue"
import SlackContactsAutoCompleteInput from "@/components/shared/SlackContactsAutoCompleteInput.vue"
import DynamicIcon from "@/components/shared/icons/DynamicIcon.vue"

export default {
  components: {
    ContactCard,
    ChannelCard,
    DynamicIcon,
    AttachmentsField,
    Form,
    TextEditor,
    SenderSlackAutoCompleteInput,
    SlackContactsAutoCompleteInput
  },
  props: {
    selectedNode: null,
    inPlaybook: {
      type: Boolean,
      default: false
    },

    currentObjectType: {
      type: String,
      required: false,
      default: null
    },
    record: {
      type: Object,
      required: false,
      default: null
    },

    autocompleteOptionsInPlaybook: {
      type: Array,
      required: false,
      default: () => []
    },

    resultReferencesListForText: null
  },
  emits: ["cancel", "submitted", "done"],
  setup() {
    const modules = [
      {
        name: "quillDelta",
        module: Delta
      }
    ]
    const initial_values = {}
    return {
      // eslint-disable-next-line vue/no-unused-properties
      initial_values,
      modules
    }
  },
  data() {
    return {
      playbook_modules: [],
      EllipsisVertical,
      body_content: "",
      selected_users: [],
      selected_channels: [],
      selected_sender: null,
      attachments: [],
      show_autocomplete_options: false
    }
  },
  computed: {
    autocomplete_options_in_playbook_with_values_for_users() {
      const list_calc = []

      this.autocompleteOptionsInPlaybook.forEach((op) => {
        const found_user = this.selected_users.find((user) => user.id == op.id)
        if (found_user && found_user.value?.email) {
          list_calc.push({ ...op, value: found_user.value })
        } else {
          list_calc.push(op)
        }
      })
      return list_calc
    },

    autocomplete_options_in_playbook_with_values_for_sender() {
      const list_calc = []

      this.autocompleteOptionsInPlaybook.forEach((op) => {
        const is_sender = this.selected_sender?.id == op.id
        if (is_sender && this.selected_sender.value?.email) {
          list_calc.push({ ...op, value: this.selected_sender.value })
        } else {
          list_calc.push(op)
        }
      })
      return list_calc
    },

    // http://localhost:8090/admin/integrations/slack

    users_list() {
      let users_list_calc = []
      const slack_obj = this.$store.state.integration.integrations.find((i) => i.name == "slack")

      if (slack_obj.users?.length) {
        users_list_calc = slack_obj.users
      }

      if (slack_obj.bot_user_id && !users_list_calc.find((u) => u.profile.first_name == "Revsetter")) {
        users_list_calc.push({
          id: "bot",
          profile: { first_name: "Revsetter" }
        })
      }

      return users_list_calc
    },
    channels_list() {
      if (this.$store.state.integration.integrations.find((i) => i.name == "slack").channels) {
        return this.$store.state.integration.integrations.find((i) => i.name == "slack").channels.user
      }

      return []
    },

    form_incomplete() {
      let form_incomplete_calc = false

      if (!this.body_content) {
        form_incomplete_calc = true
      }

      if (!this.selected_users.length && !this.selected_channels.length) {
        form_incomplete_calc = true
      }

      return form_incomplete_calc
    },

    // computed prop to tell whether the user add his gmail or outlook to the platform
    schema() {
      return Yup.object().shape({})
    }
  },
  created() {
    if (this.record) {
      this.handleAddItem(this.record, "recipients")
    }

    if (this.selectedNode?.data?.values?.body) {
      const slack_data = this.selectedNode.data.values

      const delta_calc = new Delta()
      delta_calc

      slack_data.body.ops.forEach((op) => {
        delta_calc.insert(op.insert)
      })

      this.body_content = delta_calc

      this.selected_users = []

      const found_sender = this.users_list
        .concat(this.autocompleteOptionsInPlaybook)
        .find((t) => t.id == (slack_data.sender.id ? slack_data.sender.id : slack_data.sender))

      if (slack_data.sender.email) {
        found_sender.value = {
          id: slack_data.sender.id,
          email: slack_data.sender.email.split(".")[1].replace("}", ""),
          first_name: slack_data.sender.first_name ? slack_data.sender.first_name.split(".")[1].replace("}", "") : "",
          last_name: slack_data.sender.last_name ? slack_data.sender.last_name.split(".")[1].replace("}", "") : ""
        }
      }

      if (found_sender) {
        this.selected_sender = found_sender
      }
      slack_data.to.forEach((team_member_or_contact_id) => {
        const found_team_member = this.$utils.clone(
          this.users_list
            .concat(this.autocompleteOptionsInPlaybook)
            .find((t) => t.id == (team_member_or_contact_id.id ? team_member_or_contact_id.id : team_member_or_contact_id))
        )
        if (team_member_or_contact_id.email) {
          found_team_member.value = {
            id: team_member_or_contact_id.id,
            email: team_member_or_contact_id.email.split(".")[1].replace("}", ""),
            first_name: team_member_or_contact_id.first_name ? team_member_or_contact_id.first_name.split(".")[1].replace("}", "") : "",
            last_name: team_member_or_contact_id.last_name ? team_member_or_contact_id.last_name.split(".")[1].replace("}", "") : ""
          }
        }

        if (found_team_member) {
          this.selected_users.push(found_team_member)
        }
      })

      slack_data.to.forEach((channel_id) => {
        const found_channel = this.channels_list.find((t) => t.id && t.id == channel_id)
        if (found_channel) {
          this.selected_channels.push(found_channel)
        }
      })

      // slack_data.channels.forEach((group_id) => {
      //   this.selected_channels.push(this.$store.state.data.channels.find((t) => t.id == group_id))
      // })

      const att_calc = []
      slack_data.attachments?.forEach((attachment_id) => {
        dataService.getAttachment(attachment_id).then((res) => {
          att_calc.push(res.data)
          if (att_calc.length == slack_data.attachments.length) {
            this.attachments = att_calc
          }
        })
      })
    }
  },

  methods: {
    handleSelectOption(item) {
      const body_content_calc = this.$utils.clone(this.body_content)
      body_content_calc.ops.splice(this.body_content.ops.length - 1, 1, {
        insert: this.body_content.ops[this.body_content.ops.length - 1].insert.replaceAll("\n", ` ${item.value}\n`)
      })

      this.body_content = ""
      setTimeout(() => {
        this.body_content = body_content_calc
      }, 100)

      this.show_autocomplete_options = false
    },

    handleRemoveUser(user) {
      // Logic to remove a user from the selected users list
      const index = this.selected_users.findIndex((c) => c.id === user.id)
      if (index > -1) {
        this.selected_users.splice(index, 1)
      }
    },
    handleRemoveChannel(channel) {
      // Logic to remove a channel from the selected channels list
      const index = this.selected_channels.findIndex((c) => c.id === channel.id)
      if (index > -1) {
        this.selected_channels.splice(index, 1)
      }
    },
    handleRemoveSender() {
      this.selected_sender = null
    },

    sendSlack() {
      let data = {
        to: this.selected_channels
          .map((c) => c.id)
          .concat(
            this.selected_users.map((c) => {
              if (c.value?.email) {
                const result_ref = c.id.split(".")[0].replace("${", "")
                return {
                  id: c.id,
                  email: `$\{${result_ref}.${c.value.email}}`,
                  first_name: c.value.first_name ? `$\{${result_ref}.${c.value.first_name}}` : "",
                  last_name: c.value.first_name ? `$\{${result_ref}.${c.value.last_name}}` : ""
                }
              } else {
                return c.id
              }
            })
          ),
        sender: [this.selected_sender].map((sender) => {
          if (sender.value?.email) {
            const result_ref = sender.id.split(".")[0].replace("${", "")
            return {
              id: sender.id,
              email: `$\{${result_ref}.${sender.value.email}}`,
              first_name: sender.value.first_name ? `$\{${result_ref}.${sender.value.first_name}}` : "",
              last_name: sender.value.first_name ? `$\{${result_ref}.${sender.value.last_name}}` : ""
            }
          }
          return sender.id
        })[0],
        channels: this.selected_channels.map((c) => c.id),
        object_type: this.currentObjectType,
        body: this.body_content
      }

      const promises = []
      let all_attachment_ids = []
      if (this.attachments?.length) {
        const new_attachments = this.attachments.filter((att) => typeof att.file !== "string")
        const old_attachments = this.attachments.filter((att) => typeof att.file == "string")
        all_attachment_ids = all_attachment_ids.concat(old_attachments.map((a) => a.id))

        promises.push(
          dataService.addAttachment({ attachments: new_attachments }).then((res) => {
            all_attachment_ids = all_attachment_ids.concat(res.data.map((a) => a.id))
          })
        )
      }
      Promise.all(promises).then(() => {
        data["attachments"] = all_attachment_ids

        this.$emit("done", data)
      })
    },

    handleAddItem(data) {
      if (data.type === "results") {
        this.$utils.replaceOrAddItem(this["selected_users"], data.item)
      } else if (data.type === "team_members") {
        if (this.selected_sender?.id == "bot") {
          this.$utils.replaceOrAddItem(this["selected_users"], data.item ? data.item : data)
        } else {
          this.$utils.replaceOrAddItem(this["selected_users"], data.item ? data.item : data)
        }
      } else if (data.type === "channels") {
        this.$utils.replaceOrAddItem(this["selected_channels"], data.item ? data.item : data)
      }
    }
  }
}
</script>
<style scoped>
input[type="file"] {
  display: none;
}
</style>
